// File: frontend/src/components/scoring/FilmRoomChapterView.tsx
//
// One Film Room chapter: video launcher card + clickable scoring log.
// Shared by the standalone FilmRoomPage and the ScoringInfoPage section.
// LALIGA blocks third-party embedding, so moments deep-link to the official
// upload on YouTube at the exact timestamp (minus a short lead-in), reusing
// one named companion tab.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Play } from 'lucide-react';
import { mixpanelTrack } from '@/services/mixpanel';
import { isNativePlatform, openExternalUrl } from '@/services/platform';
import { formatGametime, formatStatName } from '@/utils/formatting';
import {
  FilmRoomChapter,
  FilmRoomMoment,
  FilmRoomScore,
  chapterTotalPoints,
  momentTotal,
  previewMomentIndexesFor,
  seekLeadSeconds,
  videoSecondsFor,
} from '@/data/filmRoomChapters';

// Explicit "why this counts as creation" callout for SCA/GCA chips.
const viaLabel = (score: FilmRoomScore): string | null => {
  if (!score.via) return null;
  if (score.via === 'pass') return 'Pass';
  if (score.via === 'foul-won') return 'Foul Won';
  return score.scoreType === 'GCA' ? 'Shot Leading to Goal' : 'Shot Leading to Shot';
};

// Film Room chip colors — DELIBERATE divergence from the carousel's canonical
// category palette (owner call 2026-07-29): in the scoring-log context, color
// follows points direction, not category. Attacking = green (points earned),
// Dispossessed = red (points lost) — matching the moment-total colors on the
// right of each row. Creating keeps the carousel's light-blue text without a
// fill, while Progressing keeps its gold. Do not "fix" attacking back to
// #e24b4a here; the carousel remains the canonical reference for
// category-colored surfaces.
const CATEGORY_CHIP_CLASSES: Record<string, string> = {
  Goal: 'bg-green-500/15 text-green-400',
  ShotOnTarget: 'bg-green-500/15 text-green-400',
  Shot: 'bg-green-500/15 text-green-400',
  GCA: 'text-[#78b4e6]',
  SCA: 'text-[#78b4e6]',
  ProgPass: 'bg-accent-yellow/15 text-accent-yellow',
  ProgCarry: 'bg-accent-yellow/15 text-accent-yellow',
  Dispossessed: 'bg-red-500/15 text-red-400',
};

const VIDEO_WINDOW_NAME = 'filmRoomVideo';
const MOMENT_CUE_DURATION_MS = 2000;

const videoUrl = (videoId: string, seconds: number | null): string =>
  seconds === null
    ? `https://www.youtube.com/watch?v=${videoId}`
    : `https://www.youtube.com/watch?v=${videoId}&t=${Math.max(0, seconds)}s`;

// Deliberately no 'noopener': browsers ignore window names when it's set,
// which would open a new tab per click instead of reusing the companion tab.
// The target is youtube.com, so opener access is an acceptable trade.
const openVideo = (videoId: string, seconds: number | null): void => {
  const url = videoUrl(videoId, seconds);
  if (isNativePlatform()) {
    void openExternalUrl(url);
    return;
  }
  // Desktop gets a sized popup window docked to the right edge instead of a
  // full tab, so the Film Room log (with the moment you just clicked) stays
  // on screen beside the video. On reuse the browser ignores the features
  // string and just navigates + refocuses the existing popup, which is what
  // we want. Mobile web keeps the plain companion tab.
  if (window.matchMedia('(min-width: 1024px)').matches) {
    const width = Math.min(1100, Math.round(window.screen.availWidth * 0.55));
    const height = Math.round((width * 9) / 16) + 90;
    const left = Math.max(0, window.screen.availWidth - width - 8);
    const popup = window.open(
      url,
      VIDEO_WINDOW_NAME,
      `popup=1,width=${width},height=${height},left=${left},top=60`,
    );
    popup?.focus();
    return;
  }
  window.open(url, VIDEO_WINDOW_NAME);
};

const formatPoints = (points: number): string =>
  `${points > 0 ? '+' : ''}${Number.isInteger(points) ? points : points.toFixed(1)}`;

const formatVideoTime = (seconds: number | null): string => {
  if (seconds === null) return '--:--';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
};

interface MomentRowProps {
  moment: FilmRoomMoment;
  videoSeconds: number | null;
  runningTotal: number;
  isSelected: boolean;
  onClick: () => void;
}

const ScoringChips: React.FC<{ scores: FilmRoomScore[]; prominent?: boolean }> = ({
  scores,
  prominent = false,
}) => (
  <span className={`flex min-w-0 flex-grow flex-wrap ${prominent ? 'justify-center gap-2' : 'gap-1.5'}`}>
    {scores.map((score, i) => (
      <span
        key={i}
        className={`inline-flex max-w-full flex-wrap items-center text-left font-semibold whitespace-normal ${
          prominent
            ? 'rounded-md px-3 py-2 text-base leading-tight'
            : 'rounded px-1.5 py-0.5 text-[11px] leading-snug sm:text-xs'
        } ${
          CATEGORY_CHIP_CLASSES[score.scoreType] || 'bg-gray-500/15 text-gray-400'
        }`}
      >
        <span>{formatStatName(score.scoreType)} {formatPoints(score.points)}</span>
        {viaLabel(score) && <span className="font-normal opacity-70">&nbsp;({viaLabel(score)})</span>}
      </span>
    ))}
  </span>
);

const MomentRow: React.FC<MomentRowProps> = ({ moment, videoSeconds, runningTotal, isSelected, onClick }) => {
  const total = momentTotal(moment);
  const isGoalMoment = moment.scores.some((s) => s.scoreType === 'Goal');
  const totalClass = total > 0 ? 'text-green-400' : total < 0 ? 'text-red-400' : 'text-gray-400';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-12 w-full items-center gap-2 rounded p-2.5 text-left transition-colors sm:gap-3 ${
        isSelected
          ? 'bg-surface-raised ring-1 ring-accent-yellow/60'
          : 'bg-primary/30 hover:bg-primary/50 active:bg-primary/50'
      } ${isGoalMoment ? 'border-l-2 border-green-400' : 'border-l-2 border-transparent'}`}
      aria-label={`Watch the ${formatGametime(moment.period, moment.minute)} scoring moment on YouTube. Opens externally.`}
    >
      <span className="flex w-14 flex-shrink-0 flex-col items-end">
        <span className="font-mono text-xs text-gray-400">
          {formatGametime(moment.period, moment.minute)}
        </span>
        <span className="mt-0.5 flex items-center gap-0.5 whitespace-nowrap font-mono text-[9px] leading-3 text-gray-500">
          <Play size={7} fill="currentColor" aria-hidden="true" />
          {formatVideoTime(videoSeconds)}
          <ExternalLink size={7} aria-hidden="true" />
        </span>
      </span>
      <ScoringChips scores={moment.scores} />
      <span className="flex w-12 flex-shrink-0 flex-col items-end tabular-nums">
        <span className={`text-sm font-bold ${totalClass}`}>{formatPoints(total)}</span>
        <span className="font-mono text-[10px] leading-3 text-gray-500">{runningTotal.toFixed(1)}</span>
      </span>
    </button>
  );
};

const LogColumnHeader = () => (
  <div className="mb-1 flex items-end gap-2 px-2.5 text-[10px] font-semibold uppercase leading-3 tracking-wider text-gray-500 sm:gap-3" aria-hidden="true">
    <span className="w-14 flex-shrink-0 text-right">Time</span>
    <span className="min-w-0 flex-grow">Scoring</span>
    <span className="flex w-12 flex-shrink-0 flex-col items-end">
      <span>Pts</span>
      <span>Total</span>
    </span>
  </div>
);

interface FilmRoomChapterViewProps {
  chapter: FilmRoomChapter;
  compactPreview?: boolean;
  surface?: string;
}

const FilmRoomChapterView: React.FC<FilmRoomChapterViewProps> = ({
  chapter,
  compactPreview = false,
  surface = 'film-room-page',
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [thumbnailSrc, setThumbnailSrc] = useState(
    `https://i.ytimg.com/vi/${chapter.videoId}/maxresdefault.jpg`,
  );
  // Two-second "what to watch for" cue shown on moment click, so there is
  // enough time to read it before the external video takes your attention.
  const [cueScores, setCueScores] = useState<FilmRoomScore[] | null>(null);
  const cueTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (cueTimerRef.current !== null) window.clearTimeout(cueTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (cueTimerRef.current !== null) {
      window.clearTimeout(cueTimerRef.current);
      cueTimerRef.current = null;
    }
    setCueScores(null);
    setSelectedIndex(null);
    setIsExpanded(false);
    setThumbnailSrc(`https://i.ytimg.com/vi/${chapter.videoId}/maxresdefault.jpg`);
  }, [chapter.slug, chapter.videoId]);

  const totalPoints = useMemo(() => chapterTotalPoints(chapter), [chapter]);
  const runningTotals = useMemo(() => {
    let sum = 0;
    return chapter.moments.map((m) => (sum += momentTotal(m)));
  }, [chapter]);
  const previewIndexes = useMemo(() => previewMomentIndexesFor(chapter), [chapter]);

  const syncPending = chapter.anchors.p1 === null || chapter.anchors.p2 === null;

  const handleMomentClick = (moment: FilmRoomMoment, index: number) => {
    setSelectedIndex(index);
    setCueScores(moment.scores);
    if (cueTimerRef.current !== null) window.clearTimeout(cueTimerRef.current);
    const vs = videoSecondsFor(chapter, moment);
    const seekSeconds = vs === null ? null : vs - seekLeadSeconds(moment);
    const nativePlatform = isNativePlatform();
    const desktopWeb = !nativePlatform && window.matchMedia('(min-width: 1024px)').matches;
    cueTimerRef.current = window.setTimeout(() => {
      cueTimerRef.current = null;
      setCueScores(null);
      if (nativePlatform) {
        openVideo(chapter.videoId, seekSeconds);
      } else if (!desktopWeb) {
        // Mobile web uses same-tab navigation after the cue. A delayed
        // window.open would be rejected by Safari/Chrome as a popup.
        window.location.assign(videoUrl(chapter.videoId, seekSeconds));
      }
    }, MOMENT_CUE_DURATION_MS);
    // Desktop keeps window.open inside the click gesture so popup blockers do
    // not suppress its companion window. Native and mobile web wait for the cue.
    if (desktopWeb) openVideo(chapter.videoId, seekSeconds);
    mixpanelTrack('Film Room Moment Clicked', {
      Chapter: chapter.slug,
      Surface: surface,
      'Moment Index': index,
      Gametime: formatGametime(moment.period, moment.minute),
      'Score Types': moment.scores.map((s) => s.scoreType).join(','),
    });
  };

  const renderRows = (indexes: number[]) =>
    indexes.map((index) => {
      const moment = chapter.moments[index];
      return (
        <MomentRow
          key={`${chapter.slug}-${index}`}
          moment={moment}
          videoSeconds={videoSecondsFor(chapter, moment)}
          runningTotal={runningTotals[index]}
          isSelected={index === selectedIndex}
          onClick={() => handleMomentClick(moment, index)}
        />
      );
    });

  const firstHalfIndexes = chapter.moments
    .map((moment, index) => ({ moment, index }))
    .filter(({ moment }) => moment.period === 1 && !moment.hidden)
    .map(({ index }) => index);
  const secondHalfIndexes = chapter.moments
    .map((moment, index) => ({ moment, index }))
    .filter(({ moment }) => moment.period === 2 && !moment.hidden)
    .map(({ index }) => index);
  const visibleMomentCount = chapter.moments.filter((m) => !m.hidden).length;
  const showFullLog = !compactPreview || isExpanded;

  const handleToggle = () => {
    const nextExpanded = !isExpanded;
    setIsExpanded(nextExpanded);
    mixpanelTrack('Film Room Breakdown Toggled', {
      Chapter: chapter.slug,
      Surface: surface,
      Expanded: nextExpanded,
    });
  };

  return (
    <div>
      {cueScores && (
        <div
          className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-primary/60 px-4"
          aria-hidden="true"
        >
          <div
            className="w-full max-w-md rounded-lg bg-surface-raised p-5 shadow-xl shadow-black/20 ring-1 ring-accent-yellow/60"
            style={{ animation: 'film-room-cue-in 160ms ease-out both' }}
          >
            <ScoringChips scores={cueScores} prominent />
          </div>
        </div>
      )}
      <style>{`@keyframes film-room-cue-in{from{opacity:0;transform:scale(.98)}to{opacity:1;transform:scale(1)}}`}</style>
      {/* Video launcher: the official LALIGA upload opens on YouTube in a
          companion tab that every moment click reuses. */}
      <div className="bg-surface-card rounded-lg border border-gray-700 overflow-hidden mb-4">
        <button
          type="button"
          onClick={() => openVideo(chapter.videoId, 0)}
          className="relative block w-full group"
          aria-label="Open the full match on YouTube"
        >
          <img
            src={thumbnailSrc}
            alt={`${chapter.match.homeTeam} vs ${chapter.match.awayTeam} full match`}
            className="w-full aspect-video object-cover"
            loading="lazy"
            decoding="async"
            onError={() =>
              setThumbnailSrc(`https://i.ytimg.com/vi/${chapter.videoId}/hqdefault.jpg`)
            }
          />
          <span className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/25 transition-colors">
            <span className="flex items-center gap-2 bg-accent-yellow text-primary font-heading font-bold uppercase tracking-wider text-sm rounded-lg px-4 py-2.5">
              <img
                src="/youtube-icon-red.png"
                alt=""
                aria-hidden="true"
                className="h-6 w-auto"
              />
              Watch on YouTube
              <ExternalLink size={14} />
            </span>
          </span>
        </button>
        <div className="p-4">
          <div className="flex min-w-0 items-baseline justify-between gap-3">
            <span className="min-w-0 truncate text-lg font-heading font-bold text-white">
              {chapter.player.nickname}
            </span>
            <span className="flex-shrink-0 text-2xl font-heading font-bold text-accent-yellow tabular-nums">
              {totalPoints.toFixed(1)} pts
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {chapter.match.homeTeam} {chapter.match.homeGoals}-{chapter.match.awayGoals}{' '}
            {chapter.match.awayTeam}, {chapter.match.competition}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {chapter.blurb} Tap any scoring moment to watch the play develop on YouTube.
          </p>
          {syncPending && (
            <p className="text-micro text-accent-yellow/80 mt-2">
              Video sync for this chapter is being calibrated - moment clicks open the match from
              the start for now. Use the game clock on the broadcast to find each moment.
            </p>
          )}
        </div>
      </div>

      {/* Scoring log */}
      <div className="bg-surface-card rounded-lg border border-gray-700 p-3">
        {showFullLog ? (
          <>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              First Half
            </h3>
            <LogColumnHeader />
            <div className="space-y-1">{renderRows(firstHalfIndexes)}</div>
            <h3 className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Second Half
            </h3>
            <LogColumnHeader />
            <div className="space-y-1">{renderRows(secondHalfIndexes)}</div>
          </>
        ) : (
          <>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Featured Moments
            </h3>
            <div className="space-y-1">{renderRows(previewIndexes)}</div>
          </>
        )}

        {compactPreview && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-3">
            <span className="text-xs text-gray-500 tabular-nums">
              {isExpanded
                ? `All ${visibleMomentCount} scoring moments`
                : `${previewIndexes.length} of ${visibleMomentCount} scoring moments`}
            </span>
            <button
              type="button"
              onClick={handleToggle}
              className="inline-flex min-h-8 items-center gap-1 rounded-md px-2 text-xs font-bold text-accent-yellow transition-colors duration-150 hover:bg-accent-yellow/10 active:bg-accent-yellow/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <>Collapse breakdown <ChevronUp size={15} aria-hidden="true" /></>
              ) : (
                <>Show all {visibleMomentCount} moments <ChevronDown size={15} aria-hidden="true" /></>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilmRoomChapterView;
